/**
 * A conversa aberta: enviar (com o eco), o histórico de trás, a seleção, a
 * redação (nota, anexo, sugestão, resposta citada), o copiloto, agendamento,
 * enquete, prova e conferência de documento.
 *
 * Um pedaço do objeto de ações do `Provedor` (`estado.jsx`), cortado por área.
 * Recebe o contexto uma vez, dentro do `useMemo` de lá: a identidade de cada
 * ação continua estável, como era no objeto único.
 */
import * as api from "../api.js";
import { quemFalou } from "../conversa.js";
import { gravou } from "../gravou.js";
import { dataHora, hora } from "../visual.jsx";
import { confirmar, pedirTexto } from "@/componentes/pergunta.tsx";
import { acrescentarAnexos, esvaziar } from "./rascunhos.js";

/** Agora em UTC no formato do SQLite (`2026-08-21 18:30:00`) — o do banco. */
/*
 * O que entra no campo leva o CASO a que pertence. A redação só põe no campo o
 * que é do caso que ela está mostrando; o resto vai para a gaveta do caso dono
 * (`estado/rascunhos.js`). É o que impede um envio que terminou depois da troca
 * de conversa limpar — ou preencher — o campo do caso seguinte.
 */
const semente = (texto, caso) => ({ texto, n: Date.now(), caso });

const agoraComoOBanco = () => new Date().toISOString().replace("T", " ").slice(0, 19);

export function acoesDaConversa(c) {
  const {
    acoesRef,
    avisar,
    avisarSeFoiEnsaio,
    carregarMesa,
    carregarVista,
    casoAberto,
    lerCopiloto,
    mudar,
    navRef,
    ocupadoRef,
    recarregarCaso,
    ref,
    tentar,
  } = c;
  return {
    enviar: async (texto) => {
      const limpo = String(texto ?? "").trim();
      const anexos = ref.current.anexosPendentes;
      /* Com anexo preso, campo vazio e legitimo: "manda o documento" sem
         legenda e metade das mensagens de um atendimento. */
      if ((!limpo && !anexos.length) || !casoAberto()) return;

      /* Editar mensagem enviada não aceita arquivo — a rota só troca o texto.
         Editando com anexo preso, a edição vence e o anexo continua esperando:
         perder o arquivo escolhido para "salvar" outra coisa seria pior. */
      if (anexos.length && !ref.current.editando) {
        /* O caso de QUANDO apertou Enter: o upload leva segundos, e quem troca
           de conversa no meio mandava o segundo arquivo para o caso seguinte. */
        const caso = casoAberto();
        await tentar("enviando", async () => {
          let ultimo = null;
          /* Um por vez, na ordem em que a pessoa escolheu: em paralelo as
             fotos do documento chegariam fora de ordem no celular do cliente. */
          for (const [i, item] of anexos.entries()) {
            /* A barra por arquivo: o vídeo de 40 MB da audiência subia sem
               sinal nenhum, e "subindo" e "travou" tinham a mesma cara. */
            const andando = (fracao) =>
              mudar((a) => ({
                anexosPendentes: a.anexosPendentes.map((x) => (x.id === item.id ? { ...x, subindo: fracao ?? -1 } : x)),
              }));
            // eslint-disable-next-line no-await-in-loop -- a ordem E o requisito
            ultimo = await api.anexarComProgresso(
              caso,
              item.arquivo,
              {
                ...(i === 0 && limpo ? { legenda: limpo } : {}),
                /* A citação vai no PRIMEIRO anexo, como a legenda: respondida
                   com foto, ela se perdia e o cliente não sabia a que era. */
                ...(i === 0 && ref.current.respondendo?.externo_id ? { responde_a: ref.current.respondendo.externo_id } : {}),
                /* Figurinha é uma DECISÃO de quem manda, não uma dedução pela
                   extensão: o servidor converte a imagem para WebP 512 só
                   quando alguém pede. */
                ...(item.figurinha ? { como: "figurinha" } : {}),
                /* O recado PAUSADO entra aqui como anexo, e o tipo do arquivo
                   nem sempre atravessa o upload: sem o `como` o servidor via um
                   .webm e o WhatsApp entregava VÍDEO (Laura, 14/10). */
                ...(item.tipo.startsWith("audio/") || item.nome.startsWith("recado-") ? { como: "audio" } : {}),
              },
              andando,
            );
          }
          for (const item of anexos) if (item.url) URL.revokeObjectURL(item.url);
          /* Trocou de caso no meio: o que está na tela é o rascunho do OUTRO,
             e quem esvazia é a gaveta deste. */
          if (casoAberto() !== caso) {
            esvaziar(caso);
            await carregarMesa();
            return;
          }
          mudar({
            anexosPendentes: [],
            sementeRascunho: semente("", caso),
            sugestao: null,
            sugestaoOculta: false,
            respondendo: null,
          });
          await recarregarCaso();
          await carregarMesa();
          avisarSeFoiEnsaio(ultimo);
        });
        return;
      }

      /* Editando: o mesmo campo, o mesmo Enter — o que sai é a correção da
         mensagem que já existe, não uma mensagem nova. */
      const emEdicao = ref.current.editando;
      if (emEdicao) {
        const caso = casoAberto();
        await tentar("enviando", async () => {
          await api.editarMensagem(caso, emEdicao.id, limpo);
          mudar(casoAberto() === caso ? { editando: null, sementeRascunho: semente("", caso) } : {});
          await recarregarCaso();
        });
        return;
      }

      /* Citar é a mensagem citada viajando junto: o provedor precisa do id
         EXTERNO dela (o do WhatsApp), não do nosso. */
      const citada = ref.current.respondendo;
      const extra = citada?.externo_id ? { responde_a: citada.externo_id } : {};
      /*
       * A bolha aparece ANTES da resposta do servidor.
       *
       * Sem isto, enviar era um POST mais a releitura do caso mais a da mesa —
       * uns dois segundos na rede do escritório em que a conversa ficava
       * idêntica e o campo já vazio. Quem está no celular aperta de novo, e
       * duas mensagens iguais saem para o cliente. O eco resolve os dois: a
       * mensagem se vê na hora, com o tique de "na fila", e o texto sai do
       * campo sabendo que ele não se perde — a falha guarda o `pedido`.
       */
      const eco = {
        id: `eco-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        eco: true,
        record_id: casoAberto(),
        direcao: "saida",
        autor: ref.current.usuario?.nome ?? null,
        texto: limpo,
        /* UTC no formato do banco, que é como `mensagens.quando` é gravado
           (`datetime('now')`): a bolha do eco tem de mostrar a MESMA hora que a
           mensagem confirmada vai mostrar ao voltar do servidor. Quem formata
           para a tela continua sendo `hora()` de visual.jsx. */
        quando: agoraComoOBanco(),
        entrega: "saindo",
        citada,
        pedido: { texto: limpo, extra },
      };
      mudar((a) => ({
        pendentes: [...a.pendentes, eco],
        sugestao: null,
        sugestaoOculta: false,
        respondendo: null,
        sementeRascunho: semente("", eco.record_id),
      }));
      await acoesRef.current.despacharEco(eco);
    },

    /**
     * Manda o eco de verdade — e é o mesmo caminho do "tentar de novo".
     *
     * Falhar aqui não apaga nada: a bolha fica na conversa com o motivo e dois
     * botões. Perder o texto de uma mensagem que não saiu por causa de rede é
     * o erro que faz alguém desistir da mesa e abrir o WhatsApp no celular.
     */
    despacharEco: async (eco) => {
      const marcar = (mudanca) =>
        mudar((a) => ({ pendentes: a.pendentes.map((e) => (e.id === eco.id ? { ...e, ...mudanca } : e)) }));
      marcar({ entrega: "saindo", erro: null });
      try {
        const r = await api.enviarMensagem(eco.record_id, eco.pedido.texto, eco.pedido.extra);
        /* Enviar GRAVA antes de responder (`whatsapp.enviar` guarda a linha e
           só então volta), então a releitura abaixo já traz a mensagem de
           verdade — e o eco sai de cena sem precisar casar id nenhum. */
        marcar({ entrega: "enviada" });
        /* A releitura traz a mensagem de verdade e `juntarConversa` já esconde
           o eco correspondente no mesmo quadro — tirar o eco do estado aqui é
           só limpeza, e não muda um pixel na tela. */
        await recarregarCaso();
        avisarSeFoiEnsaio(r);
        mudar((a) => ({ pendentes: a.pendentes.filter((e) => e.id !== eco.id) }));
        /* A lista da mesa por último: ela repinta a coluna da esquerda, e
           fazê-la antes atrasava a bolha da conversa que a pessoa está
           olhando. */
        await carregarMesa();
      } catch (e) {
        marcar({ entrega: "falhou", erro: e.message });
        avisar(e.message);
      }
    },

    /** Tira o eco que falhou, devolvendo o texto ao campo — nada se perde. */
    voltarEcoAoCampo: (id) =>
      mudar((a) => {
        const eco = a.pendentes.find((e) => e.id === id);
        if (!eco) return {};
        return {
          pendentes: a.pendentes.filter((e) => e.id !== id),
          sementeRascunho: semente(eco.pedido.texto, eco.record_id),
          /* A citação só volta se o eco é do caso aberto: é mensagem DAQUELA conversa. */
          ...(eco.record_id === casoAberto() ? { respondendo: eco.citada ?? null } : {}),
        };
      }),
    /* Descartar joga fora o texto que a pessoa escreveu e o servidor nunca
       recebeu: não existe onde ir buscar depois. Por isso pergunta, dizendo
       que "Editar" é a saída de quem só quer o texto de volta. */
    descartarEco: async (id) => {
      const ok = await confirmar({
        titulo: "Descartar a mensagem que não saiu?",
        texto: 'O texto sai da conversa e não volta. Para aproveitá-lo, use "Editar".',
        acao: "Descartar",
        perigo: true,
      });
      if (ok) mudar((a) => ({ pendentes: a.pendentes.filter((e) => e.id !== id) }));
    },
    tentarEcoDeNovo: (id) => {
      const eco = ref.current.pendentes.find((e) => e.id === id);
      if (eco) acoesRef.current.despacharEco(eco);
    },

    /**
     * O pedaço anterior da conversa. Quem chama é a rolagem, ao chegar no topo.
     *
     * Devolve quantas entraram: a trilha precisa disso para não perder o lugar
     * — ela mede a altura antes e depois e devolve a rolagem à mesma linha.
     */
    carregarAntigas: async () => {
      const a = ref.current;
      const id = casoAberto();
      if (!id || a.antigasFim || ocupadoRef.current === "antigas") return 0;
      const conhecidas = [...a.antigas, ...(a.caso?.mensagens ?? [])];
      /* O id da mais antiga que já temos — sem ele o servidor devolveria de
         novo a mesma página, e a rolagem entraria em laço. */
      const antes = conhecidas.reduce((menor, m) => (typeof m.id === "number" && (!menor || m.id < menor) ? m.id : menor), null);
      if (!antes) return 0;
      const r = await tentar("antigas", () => api.conversaAntes(id, antes));
      if (!r) return 0;
      /* Trocou de caso enquanto a página vinha: joga fora em silêncio. */
      if (casoAberto() !== id) return 0;
      mudar((atual) => ({ antigas: [...r.mensagens, ...atual.antigas], antigasFim: Boolean(r.fim) }));
      return r.mensagens.length;
    },

    /* ------------------------------------------- seleção e busca na conversa */

    /** Abre o modo seleção já com a primeira mensagem marcada. */
    selecionar: (id) =>
      mudar((a) => {
        const antes = a.selecionadas ?? [];
        return { selecionadas: antes.includes(id) ? antes.filter((x) => x !== id) : [...antes, id] };
      }),
    sairDaSelecao: () => mudar({ selecionadas: null }),
    /** Aponta uma mensagem: a trilha rola até ela e a bolha pisca. */
    apontarMensagem: (id) => {
      mudar({ apontada: id });
      /* O realce é um piscar, não um estado: deixado ligado, a bolha ficaria
         amarela até alguém clicar em outra. */
      setTimeout(() => mudar((a) => (a.apontada === id ? { apontada: null } : {})), 2200);
    },

    /** Editar é o texto voltando para o campo, com a barra dizendo o que é. */
    abrirEdicao: (mensagem) =>
      mudar({ editando: mensagem, respondendo: null, sementeRascunho: semente(mensagem.texto ?? "", casoAberto()) }),
    pararDeEditar: () => mudar({ editando: null, sementeRascunho: semente("", casoAberto()) }),

    /** Encaminhar abre a escolha da conversa de destino. */
    /*
     * Encaminhar carrega uma LISTA, mesmo quando é uma só.
     *
     * A rota sempre aceitou lote (`mensagens: []`); era a tela que só sabia
     * pedir uma. Com o modo seleção de pé, o print + o áudio + o número do
     * processo vão para o advogado num gesto, e não em três aberturas do
     * mesmo modal.
     */
    abrirEncaminhar: (mensagem) =>
      mudar({ encaminhando: { mensagens: [mensagem.id], resumo: String(mensagem.texto ?? mensagem.arquivo ?? "anexo") } }),
    abrirEncaminharVarias: () => {
      const ids = ref.current.selecionadas ?? [];
      if (!ids.length) return;
      mudar({
        encaminhando: { mensagens: ids, resumo: `${ids.length} mensagens selecionadas` },
        selecionadas: null,
      });
    },
    fecharEncaminhar: () => mudar({ encaminhando: null }),

    /*
     * Pedir ao ASSISTENTE a partir da conversa — uma mensagem ou as marcadas.
     *
     * Mesma forma do encaminhar (uma lista, mesmo quando é uma só) porque o
     * pedido é sobre o TRECHO: "cria o lembrete disso aqui" pode citar a
     * mensagem em que o cliente marcou a hora e a de antes, em que ele disse o
     * motivo.
     */
    pedirAoAssistente: (ids) => mudar({ pedindoAoAssistente: ids.filter(Boolean) }),
    pedirDasSelecionadas: () => {
      const ids = ref.current.selecionadas ?? [];
      if (!ids.length) return;
      mudar({ pedindoAoAssistente: ids, selecionadas: null });
    },
    fecharPedidoAoAssistente: () => mudar({ pedindoAoAssistente: null }),

    /*
     * As fotos selecionadas viram um PDF só.
     *
     * O gesto da semana: a CTPS chega em cinco fotos e o processo precisa de
     * um arquivo. O PDF nasce na pasta do cliente e aparece na conversa — de
     * onde já dá para mandar de volta ou anexar à peça.
     */
    juntarEmPdf: async () => {
      const ids = ref.current.selecionadas ?? [];
      if (!ids.length) return;
      const titulo = await pedirTexto({
        titulo: `Juntar ${ids.length} foto(s) em PDF`,
        texto: "Uma por página, na ordem da conversa. O arquivo fica na pasta do cliente.",
        valor: `Documentos de ${ref.current.caso?.registro?.nome ?? ""}`.trim(),
        acao: "Juntar",
      });
      if (!titulo) return;
      await tentar("enviando", async () => {
        const r = await api.juntarEmPdf(casoAberto(), ids, titulo);
        mudar({ selecionadas: null });
        await recarregarCaso();
        avisar(
          r.fora?.length
            ? `PDF com ${r.paginas} página(s). Fora: ${r.fora.join(", ")}.`
            : `PDF com ${r.paginas} página(s) na pasta do cliente.`,
        );
      });
    },

    /**
     * Apaga para todos o que está selecionado, uma por uma e na ordem.
     *
     * guarda-ok: pergunta — quem pergunta é a barra da seleção
     * (`SelecaoDeMensagens.jsx`), porque só ela sabe QUANTAS são, e o número
     * é o que faz a pessoa parar antes de confirmar.
     */
    apagarVarias: () =>
      tentar("mensagem", async () => {
        const ids = ref.current.selecionadas ?? [];
        let falhas = 0;
        for (const id of ids) {
          /* eslint-disable-next-line no-await-in-loop -- em paralelo o provedor
             responde com limite de taxa e metade some sem dizer qual. */
          await api.apagarMensagem(casoAberto(), id).catch(() => falhas++);
        }
        mudar({ selecionadas: null });
        await recarregarCaso();
        avisar(falhas ? `${ids.length - falhas} apagadas, ${falhas} falharam.` : `${ids.length} apagadas para todos.`);
      }),

    /**
     * O texto das selecionadas na área de transferência.
     *
     * Com a hora e o autor: o destino disso é o e-mail para o cliente e a
     * seção de fatos da peça, onde "quem disse e quando" é metade do valor.
     */
    copiarVarias: async () => {
      const ids = new Set(ref.current.selecionadas ?? []);
      const todas = [...(ref.current.antigas ?? []), ...(ref.current.caso?.mensagens ?? [])];
      const linhas = todas
        .filter((m) => ids.has(m.id))
        /* O nome de quem falou, o mesmo da bolha (`quemFalou`): assinar tudo
           como "Escritório" apagava a diferença entre o que a advogada
           escreveu e o que o copiloto propôs — e o destino disto é o e-mail
           ao cliente e a seção de fatos da peça. */
        .map(
          (m) =>
            `[${dataHora(m.quando)}] ${quemFalou(m, ref.current.caso, ref.current.equipe).nome}: ${m.texto ?? m.arquivo ?? ""}`,
        );
      if (!linhas.length) return;
      try {
        await navigator.clipboard.writeText(linhas.join("\n"));
        avisar(linhas.length === 1 ? "1 mensagem copiada." : `${linhas.length} mensagens copiadas.`);
        mudar({ selecionadas: null });
      } catch {
        /* Sem permissão de área de transferência (http, navegador antigo) o
           honesto é dizer, não engolir e deixar a pessoa colar o nada. */
        avisar("O navegador não deixou copiar. Selecione o texto na conversa.");
      }
    },
    /*
     * "Já vi estes avisos" — o sino zerando de verdade.
     *
     * O aviso é conta feita na hora (prazo aberto, cliente sem resposta, o que
     * o copiloto mexeu), então não havia linha para marcar como lida: o balão
     * mostrava as mesmas coisas para sempre. Manda-se a chave do que está NA
     * TELA — dispensar o que ninguém viu seria apagar aviso por antecipação —
     * e a lista é relida das duas fontes (o sino vem com a mesa).
     */
    marcarAvisosLidos: async (avisos) => {
      const chaves = (avisos ?? []).map((a) => a.chave).filter(Boolean);
      if (!chaves.length) return;
      /* A lista some na hora: esperar a volta do servidor para apagar uma
         notificação já lida é o tipo de espera que faz clicar duas vezes. */
      mudar((a) => ({
        avisos: a.avisos.filter((x) => !chaves.includes(x.chave)),
        avisosTodos: a.avisosTodos ? a.avisosTodos.filter((x) => !chaves.includes(x.chave)) : a.avisosTodos,
      }));
      await tentar("avisos", async () => {
        await api.marcarAvisosLidos(chaves);
        /* Relê: o teto de doze do sino esconde o que estava atrás dos lidos. */
        await carregarMesa();
        if (navRef.current.vista === "avisos") await carregarVista("avisos");
      });
    },

    abrirPeticao: () => navRef.current.ajustar({ peticao: true }),

    /** Põe a mensagem na barra de citação — quem manda é o `enviar`. */
    responderMensagem: (mensagem) => mudar({ respondendo: mensagem }),
    pararDeResponder: () => mudar({ respondendo: null }),

    escreverNoRascunho: (texto) => mudar({ sementeRascunho: semente(texto, casoAberto()), sugestaoOculta: true }),

    /*
     * Trocar o destino do que está escrito, sem perder o que está escrito.
     *
     * Voltar para `cliente` NÃO limpa o campo: quem trocou por engano teria de
     * digitar de novo. Quem quer descartar apaga — é o gesto normal do campo.
     */
    alternarNota: () => mudar((a) => ({ modoComposer: a.modoComposer === "nota" ? "cliente" : "nota" })),

    notaInterna: async (texto) => {
      const nota = String(texto ?? "").trim();
      /* Anexo preso não vai em nota: a rota só grava texto na trilha. Dizer
         antes é o que impede a pessoa achar que mandou o documento. */
      if (ref.current.anexosPendentes.length)
        return avisar("Nota interna não leva anexo — mande os arquivos ao cliente ou tire-os do campo.");
      /* Sem caixa de texto por cima: o campo do composer É onde se escreve, e
         o aviso aponta para ele em vez de abrir outro lugar para digitar. */
      if (!nota) {
        mudar({ modoComposer: "nota" });
        return avisar("Escreva a nota no campo abaixo — ela fica na trilha e o cliente não vê.");
      }
      const caso = casoAberto();
      await tentar("nota", async () => {
        await api.anotar(caso, nota);
        mudar({ sementeRascunho: semente("", caso), ...(casoAberto() === caso ? { modoComposer: "cliente" } : {}) });
        await recarregarCaso();
      });
    },

    /**
     * Escolher arquivo NAO envia: prende ao composer.
     *
     * `multiple` porque documento de cliente vem em tres fotos, e repetir o
     * gesto tres vezes e o que faz alguem desistir e mandar pelo WhatsApp
     * pessoal. A legenda escrita no campo vai junto do primeiro — e o que o
     * WhatsApp faz com varios arquivos e uma legenda.
     */
    anexar: () => {
      const escolha = document.createElement("input");
      escolha.type = "file";
      escolha.multiple = true;
      escolha.onchange = () => acoesRef.current.receberArquivos(escolha.files);
      escolha.click();
    },

    /**
     * Arquivos entrando no composer, venham de onde vierem.
     *
     * Três portas para o mesmo lugar: o clipe, arrastar para cima da conversa e
     * colar (Ctrl+V com print na área de transferência, que é como metade dos
     * prints do dia chega). Antes só o clipe existia, e o print tinha de ser
     * salvo em disco para depois ser escolhido de novo.
     */
    receberArquivos: (arquivos, paraCaso) => {
      const novos = [...(arquivos ?? [])]
        /* Pasta arrastada chega como item sem tipo e com tamanho zero: o envio
           falharia no servidor sem dizer por quê. */
        .filter((arquivo) => arquivo && arquivo.size > 0)
        .map((arquivo) => ({
          /* Id proprio: dois arquivos com o mesmo nome sao dois anexos, e a
             chave do React nao pode ser o nome nem o indice (tirar o do meio
             remontava a lista errada). */
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          arquivo,
          /* Print colado vem sem nome ("image.png" genérico ou vazio): o nome
             com hora é o que deixa a pasta do cliente legível depois. */
          nome: arquivo.name || `colado-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.png`,
          tipo: arquivo.type || "",
          tamanho: arquivo.size,
          url: URL.createObjectURL(arquivo),
        }));
      if (!novos.length) return;
      /* O recado pausado ao trocar de caso chega depois da troca: vai para a
         gaveta do caso em que foi gravado, não para o que abriu. */
      if (paraCaso && paraCaso !== casoAberto()) return acrescentarAnexos(paraCaso, novos);
      mudar((a) => ({ anexosPendentes: [...a.anexosPendentes, ...novos] }));
    },

    /* Liga e desliga "mandar como figurinha" no anexo preso. Vale por arquivo:
       a pessoa pode mandar a foto do contrato e a figurinha na mesma leva. */
    alternarFigurinha: (id) =>
      mudar((a) => ({
        anexosPendentes: a.anexosPendentes.map((x) => (x.id === id ? { ...x, figurinha: !x.figurinha } : x)),
      })),

    tirarAnexoPendente: (id) =>
      mudar((a) => {
        const fora = a.anexosPendentes.find((x) => x.id === id);
        if (fora?.url) URL.revokeObjectURL(fora.url);
        return { anexosPendentes: a.anexosPendentes.filter((x) => x.id !== id) };
      }),

    pedirSugestao: async (outra = false) => {
      const variacao = outra ? ref.current.sugestaoIdx + 1 : ref.current.sugestaoIdx;
      mudar({ sugestaoOculta: false, sugestaoIdx: variacao });
      const caso = casoAberto();
      await tentar("sugestao", async () => {
        const sugestao = await api.sugerir(caso, variacao);
        /* Chegou depois da troca de conversa: é sugestão para OUTRO cliente. */
        if (casoAberto() === caso) mudar({ sugestao: { ...sugestao, caso } });
      });
    },
    usarSugestao: () =>
      mudar((a) => ({
        sementeRascunho: semente(a.sugestao?.texto ?? "", a.sugestao?.caso ?? casoAberto()),
        sugestaoOculta: true,
      })),
    ocultarSugestao: () => mudar({ sugestaoOculta: true }),

    extrairFatos: () =>
      tentar("fatos", async () => {
        const r = await api.extrairFatos(casoAberto());
        avisar(`${r.fatos.length} fato(s) extraído(s) da conversa.`);
        await recarregarCaso();
      }),

    /* Ler o caso de novo do zero. Sem isto, caso parado = mesma frase para
       sempre, e a leitura de meia hora atrás passa por conclusão de agora. */
    relerCopiloto: () => tentar("copiloto", () => lerCopiloto(casoAberto(), { renovar: true })),

    acaoDoCopiloto: async (marca) => {
      if (marca === "PEÇA") return navRef.current.ajustar({ peticao: true });
      /* Levar à aba, não ligar o microfone: cartão de sugestão que começa a
         gravar é armadilha, e quem grava é o botão que mostra que está gravando. */
      if (marca === "REUNIÃO") return navRef.current.ajustar({ painel: "reunioes" });
      mudar({ sugestaoOculta: false });
      return tentar("sugestao", async () => {
        mudar({ sugestao: await api.sugerir(casoAberto(), marca === "DOC" ? 0 : 1) });
      });
    },

    /*
     * Enquete pelo WhatsApp — duas perguntas, sem tela nova.
     *
     * Metade do que o atendimento pergunta é escolha ("qual o melhor
     * horário?"), e a resposta em texto livre ainda precisa de gente para
     * interpretar. As opções vêm uma por linha porque é como se escreve uma
     * lista sem inventar um editor para ela.
     */
    /*
     * Agendar o que está escrito no campo.
     *
     * O texto vem do rascunho e não de uma caixa nova: quem agenda já escreveu
     * a mensagem — pedir para digitar de novo dentro do diálogo seria o
     * caminho mais curto para agendar coisa diferente da que estava na tela.
     */
    agendarMensagem: async (texto) => {
      const limpo = String(texto ?? "").trim();
      if (!limpo) return avisar("Escreva a mensagem primeiro — o agendamento guarda o que está no campo.");
      const caso = casoAberto();

      /* Sugere daqui a uma hora, no relógio de quem está olhando: é o valor que
         o campo nativo entende (`YYYY-MM-DDTHH:mm`, hora local). */
      const daquiAUmaHora = new Date(Date.now() + 60 * 60 * 1000 - new Date().getTimezoneOffset() * 60 * 1000)
        .toISOString()
        .slice(0, 16);
      const quando = await pedirTexto({
        titulo: "Quando esta mensagem deve sair?",
        texto: `"${limpo.length > 90 ? `${limpo.slice(0, 89)}…` : limpo}"`,
        tipo: "datetime-local",
        valor: daquiAUmaHora,
        acao: "Agendar",
      });
      if (!quando) return;

      await tentar("enviando", async () => {
        /* O campo nativo devolve hora LOCAL sem fuso; `new Date` a interpreta
           no fuso de quem marcou, que é o certo — o servidor guarda em UTC. */
        await api.agendarMensagem(caso, limpo, new Date(quando).toISOString());
        mudar({ sementeRascunho: semente("", caso) });
        await recarregarCaso();
        avisar("Mensagem agendada. Ela sai sozinha na hora marcada.");
      });
    },

    enviarAgendadaAgora: async (agendada) => {
      await tentar("enviando", async () => {
        await api.enviarAgendadaAgora(casoAberto(), agendada);
        await recarregarCaso();
        avisar("Mensagem enviada agora.");
      });
    },

    cancelarAgendada: async (agendada) => {
      const ok = await confirmar({
        titulo: "Cancelar este envio?",
        texto: "A mensagem não sai na hora marcada. O texto fica na trilha do caso.",
        acao: "Cancelar envio",
        perigo: true,
      });
      if (!ok) return;
      await tentar("enviando", async () => {
        await api.cancelarAgendada(casoAberto(), agendada);
        await recarregarCaso();
        avisar("Envio cancelado.");
      });
    },

    enviarEnquete: async () => {
      const pergunta = await pedirTexto({
        titulo: "Enquete no WhatsApp",
        texto: "O cliente responde tocando na opção, sem digitar.",
        convite: "Qual é a pergunta?",
        acao: "Continuar",
      });
      if (!pergunta?.trim()) return;

      const cru = await pedirTexto({
        titulo: "Opções da enquete",
        texto: "Uma por linha. De duas a doze.",
        convite: "Manhã\nTarde\nNoite",
        linhas: 5,
        acao: "Enviar",
      });
      const opcoes = (cru ?? "")
        .split("\n")
        .map((o) => o.trim())
        .filter(Boolean);
      if (opcoes.length < 2) return;

      await tentar("enviando", async () => {
        await api.enviarEnquete(casoAberto(), pergunta.trim(), opcoes);
        /* A enquete é mensagem: sem reler, ela só aparecia quando um evento
           qualquer passasse pela conversa. */
        await recarregarCaso();
      });
    },

    marcarProva: async (mensagem) => {
      const rotulo = await pedirTexto({
        titulo: "Marcar como prova",
        texto: "O rótulo é como o trecho aparece na peça.",
        valor: (mensagem.texto ?? "").slice(0, 40),
        acao: "Marcar",
      });
      if (!rotulo) return;
      await tentar("prova", async () => {
        await api.anotarProva({
          record_id: casoAberto(),
          rotulo,
          detalhe: mensagem.texto ?? mensagem.transcricao ?? "",
          especie: "Prova",
          origem: "mensagem",
          mensagem_id: mensagem.id,
          fonte: `msg ${hora(mensagem.quando)}`,
        });
        await recarregarCaso();
      });
    },

    /*
     * Conferir um documento do checklist, de dentro do atendimento.
     *
     * O checklist era leitura pura na única tela onde a pessoa está OLHANDO o
     * que o cliente mandou: para dizer "esse holerite está ilegível" era preciso
     * sair da conversa. E é este estado que a régua de cobrança lê para decidir
     * quem está parado esperando documento.
     */
    conferirDocumento: (item, estado) =>
      tentar("checklist", async () => {
        await api.conferirDocumento(item, estado);
        await recarregarCaso();
        /* O verde pisca NA LINHA do documento. O balão do rodapé continua,
           para quem não estava olhando para cá — mas quem estava vê a
           confirmação no lugar onde ela aconteceu, com o nome de quem
           conferiu. Ver `gravou.js`. */
        gravou(`checklist:${item}`, ref.current.usuario?.nome);
      }),
  };
}
